import Axios from 'axios';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import getPath from 'lodash/get';
import { authManager, userManager } from '../helpers/managers';
import { addInvitedSoundcastsToUser } from './add_soundcast_to_user';
import { getCookie } from '../helpers/cookies';

// bandaid solution to prevent certain emails to signin
const bannedEmails = ['xmbrown2000@yahoo.com'];
const isBannedAccount = email => bannedEmails.includes(email);
const handleBannedAccounts = (user = {}) => {
  return new Promise(async resolve => {
    // bandaid solution to prevent certain emails to signin
    if (isEmpty(user)) {
      return resolve();
    }

    if (isBannedAccount(user.email)) {
      await authManager.signOut();

      const isBannedUserExists = await userManager.exists(user.uid);
      if (isBannedUserExists) {
        await userManager.remove(user.uid);
      }

      await user.delete();
      return window.location.reload();
    }

    resolve();
  });
};

const signInPassword = (email, password) => {
  return new Promise(async (resolve, reject) => {
    try {
      await authManager.signInWithEmailAndPassword(email, password);
      const { uid: userId } = authManager.getCurrentUser() || {};
      if (!isEmpty(userId)) {
        let user = await userManager.getById(userId) || {};
        if (isEmpty(user) || isEmpty(user.email)) {
          // try to create and reload new listener
          await Axios.post('/api/complete_sign_up', {
            email,
            firstName: user.firstName || 'Listener',
            lastName: user.lastName || 'Listener',
            xsrc: 'webapp',
          });
          user = await userManager.getById(userId);
        }
        resolve(user);
      } else {
        reject(new Error('Failed to save login info. Please try again later.'));
        // Raven.captureMessage('Failed to save login info. Please try again later.');
      }
    } catch (error) {
      reject(error);
      console.log(`Error: signInPassword`, error.toString());
    }
  });
};

const signInFacebook = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await authManager.signInWithFacebookPopup();

      // This gives you a Facebook Access Token. You can use it to access the Facebook API.
      // The signed-in user info.
      const userEmail = result && result.user && result.user.email;
      if (userEmail) {
        // check if user already registered
        // TODO see #212
      }

      const user = authManager.getCurrentUser();
      if (isEmpty(user)) {
        return reject(new Error('Unable to read logged in user'));
      }

      // verify banned accounts
      await handleBannedAccounts(user);

      // check if the user already exist
      const userId = user.uid;
      const isUserExists = await userManager.exists(userId);
      // if not exists, its a new user
      if (!isUserExists) {
        // if there's no data in users, remove them
        // as we no longer save this auth to our db
        try {
          await user.delete();
        } catch (err) {
          console.log('Unable to delete user', err.message);
        }

        return resolve({});
      }

      const userInfo = await userManager.getById(userId);
      const photoURL = `${result.user.photoURL}?width=200`;
      await userManager.saveProp(userId, 'pic_url', photoURL);

      resolve({
        ...userInfo,
        pic_url: photoURL,
      });
    } catch (error) {
      reject(error);
    }
  });
};

const facebookErrorCallback = (error, callback) => {
  // Handle Errors here.
  if (error.code === 'auth/account-exists-with-different-credential') {
    // Step 2.
    // User's email already exists.
    console.log('Error signInFacebook', error);
    // The provider account's email address.
    const email = error.email;
    // Get registered providers for this email.
    authManager
      .fetchSignInMethodsForEmail(email)
      .then(providers => {
        // Step 3.
        // If the user has several providers,
        // the first provider in the list will be the "recommended" provider to use.
        if (providers && providers[0] === 'password') {
          // Asks the user his password.
          // In real scenario, you should handle this asynchronously.
          const password = prompt('Please enter your Soundwise password'); // TODO: implement promptUserForPassword.
          authManager
            .signInWithEmailAndPassword(email, password)
            .then(result => {
              // Step 4a.
              // The pending Facebook credential.
              return result && result.user && result.user.linkWithCredential(error.credential);
            })
            .then(callback);
        }
      });
  } else if (error.code === 'auth/invalid-credential') {
    console.error(error.message);
    callback();
  }
};

const signupCommon = (_user, data = {}) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { publisherId = null, isListenerDirectSignup = false, withTour = true } = data || {};
      const firstName = _user.firstName || localStorage.getItem('soundwiseSignupFName');
      const lastName = _user.lastName || localStorage.getItem('soundwiseSignupLName');
      const email = _user.email || localStorage.getItem('soundwiseSignupEmail');
      const password = _user.password;
      const avatar =
        _user.pic_url || 'https://soundwiseinc.s3.amazonaws.com/user_profile_pic_placeholder.png';

      const userToSave = {
        firstName,
        lastName,
        email: [email],
        password,
        pic_url: avatar,
        created: moment().unix(),
      };

      // add admin fields
      if (publisherId) {
        userToSave.admin = true;
        userToSave.publisherID = publisherId;

        // tours should only for publishers for now
        userToSave.withTour = withTour;
      }

      // if its a listener signup mode
      if (isListenerDirectSignup) {
        userToSave.isListenerDirectSignup = true;
      }

      // get the contract id from lmref if present
      const lmref = getCookie('lmref');
      if (!isEmpty(lmref) && 'atob' in window) {
        const lmrefData = JSON.parse(window.atob(lmref));
        const contractId = getPath(lmrefData, 'click.contract.id', 'unknown');
        userToSave.referredBy = `lm_${contractId}`;
      }

      try {
        // save user via API
        await Axios.post('/api/user', userToSave);
      } catch (err) {
        console.log('Unable to save user', userToSave, err.message);
      } finally {
        await authManager.signInWithEmailAndPassword(email, password);
        const authUser = authManager.getCurrentUser();
        const userInfo = {
          id: authUser.uid,
          ...userToSave,
        };

        // check invitations for created user
        await addInvitedSoundcastsToUser(userInfo);

        resolve(userInfo);
      }
    } catch (err) {
      reject(err);
    }
  });
};

export {
  isBannedAccount,
  handleBannedAccounts,
  signInPassword,
  signInFacebook,
  facebookErrorCallback,
  signupCommon,
};
